import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import Box from '@repo/ui/box'
import { HeaderIcon, SearchIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Kanban, {
  KanbanColumnProps,
  KanbanItemRequiredFields,
  KanbanUpdatedData
} from '@repo/ui/kanban'
import NoteBox from '@repo/ui/note-box'
import ScrollableDiv from '@repo/ui/scrollable-div'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { getPcr } from '../../api/pcr/get-pcr'
import { getPcrStaffApplications } from '../../api/pcr/get-pcr-staff-applications'
import { getLatestPcrPayScale } from '../../api/pcr-pay-scale/get-latest-pcr-pay-scale'
import { updateScoreCard } from '../../api/score-cards/update-score-card'
import { changeStaffApplicationStatus } from '../../api/staff-application/change-application-status'
import { getStaffApplicationCurrentScoreCard } from '../../api/staff-application/get-staff-application-current-score-card'
import { getStaffApplications } from '../../api/staff-application/get-staff-applications'
import UserNavBar from '../../components/elements/nav-bar'
import KanbanTaskCard from '../../components/pages/pcr/kanban-task-card'
import {
  formatPcrSalary,
  getPcrApplicationSalary
} from '../../components/pages/pcr/pcr-pay-scale.utils'
import { PcrScoreCardModal } from '../../components/pages/pcr/pcr-score-card-form/pcr-score-card-form'
import {
  contactSectionMapping,
  getPreviousTab,
  mapScoreCardToInput,
  staffApplicationStatusesIndexMapping
} from '../../components/pages/staff-application/utils'
import useAuth from '../../lib/hooks/use-auth'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useMemorizedDebounce from '../../lib/hooks/useMemorizedDebounce'
import {
  StaffApplicationKanbanStatus,
  staffApplicationKanbanStatusMapping
} from '../../lib/types/enums/staff-application/staff-application-kanban-status.enum'
import { StaffApplicationStatusEnum } from '../../lib/types/enums/staff-application/staff-application-status.enum'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import {
  StaffApplicationFilters,
  StaffApplicationLight
} from '../../lib/types/models/staff-application/staff-application'
import { reorderKanbanItems } from '../../lib/utils/functions/kanban.function'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

interface KanbanProps extends KanbanItemRequiredFields<StaffApplicationKanbanStatus> {
  data: StaffApplicationLight
}

export const applicationsInitialFilter: StaffApplicationFilters = {
  periods: [],
  diplomas: [],
  languages: [],
  statuses: [],
  types: [],
  evaluations: [],
  similarApplications: false
}

const pcrSalaryTotalStatuses = new Set<StaffApplicationKanbanStatus>([
  StaffApplicationStatusEnum.CLASSIFIED,
  StaffApplicationStatusEnum.INTERVIEW_DONE
])

/**
 * Applications with these statuses stay visible in the "Entretien terminé" column until they are
 * paid, so that the PCR can still be paid for them. Their status must never be rewritten by the
 * kanban drag and drop.
 */
const pcrKanbanLockedStatuses = new Set<StaffApplicationStatusEnum>([
  StaffApplicationStatusEnum.ACCEPTED,
  StaffApplicationStatusEnum.REJECTED_AFTER_INTERVIEW
])

const PcrKanbanPage = () => {
  const previousTab = getPreviousTab()
  const [search, setSearch] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [disableScroll, setDisableScroll] = useState(false)

  const queryClient = useQueryClient()

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'scoreCard',
    { id: number }
  >(['scoreCard'])

  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const pcrId = user?.pcr?.id || id

  const { data: pcr } = useQuery({
    queryKey: ['pcr', pcrId],
    queryFn: () => {
      if (id) return getPcr(Number(pcrId))
    }
  })

  // Debounce the inputValue
  const debouncedSearch = useMemorizedDebounce(inputValue, 500)

  const { data: applications, refetch: refetchApplications } = useQuery({
    queryKey: ['staff-application-kanban', search, 'LIGHT'],
    queryFn: () => {
      if (pcrId && Number(pcrId) > 0) {
        if (user?.pcr?.id) return getPcrStaffApplications({ search, version: 'LIGHT' }, +pcrId)
        return getStaffApplications({
          search,
          version: 'LIGHT',
          filter: { pcrId: +pcrId }
        })
      }
    }
  })

  const { data: payScale } = useQuery({
    queryKey: ['latestPcrPayScale'],
    queryFn: getLatestPcrPayScale
  })

  const { mutateAsync: updateStaffApplicationStatus } = useMutation({
    mutationFn: changeStaffApplicationStatus,
    onError: showAxiosError,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['score-card'], exact: true })
  })

  const {
    data: scoreCard,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['score-card', selectedData?.id],
    queryFn: () => {
      if (selectedData) return getStaffApplicationCurrentScoreCard(selectedData.id)
    },
    enabled: !!selectedData
  })

  const { mutate, isPending } = useMutation({
    mutationFn: updateScoreCard,
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      refetchApplications()
      closeModel('scoreCard')
    }
  })

  const isLockedApplication = (id: number) => {
    const application = applications?.data?.find((item) => item.id === id)

    return !!application && pcrKanbanLockedStatuses.has(application.status)
  }

  const handleUpdateChange = async (data: KanbanUpdatedData<KanbanProps>) => {
    if (!user) return false
    const { newState, oldState, oldItem, newItem } = data

    if (oldItem.columnId === 'PAID' || newItem.columnId === 'PAID') return false

    // an accepted or rejected after interview application is only displayed in the
    // "Entretien terminé" column, it cannot be moved since its status must not change
    if (isLockedApplication(Number(oldItem.id))) return false

    const updatedData = reorderKanbanItems<StaffApplicationKanbanStatus>(newState, oldState)

    if (newItem.columnId !== oldItem.columnId) {
      // update the scorecard's status of the newItem
      // get the item from the updatedData array
      const item = updatedData.find((item) => item.id === Number(newItem.id))

      if (item && item.columnId !== 'PAID') {
        await updateStaffApplicationStatus({
          id: Number(newItem.id),
          status: item.columnId,
          order: item.order
        })
        queryClient.invalidateQueries({
          queryKey: ['score-card', Number(newItem.id)],
          exact: true
        })
      }

      toggleModal('scoreCard', { id: Number(newItem.id) })
      // when we set the selected data, we check if the scorecard's status is one of the three following:
      // - FIRST_CONTACT_ESTABLISHED
      // - SECOND_CONTACT_ESTABLISHED
      // - THIRD_CONTACT_ESTABLISHED
      // if it is, update the url of the page to include the scroll to the contact section
      // and set the previous tab index
      if (
        newItem.columnId === StaffApplicationStatusEnum.FIRST_CONTACT_ESTABLISHED ||
        newItem.columnId === StaffApplicationStatusEnum.SECOND_CONTACT_ESTABLISHED ||
        newItem.columnId === StaffApplicationStatusEnum.THIRD_CONTACT_ESTABLISHED
      ) {
        const contactSection = contactSectionMapping[newItem.columnId]
        if (contactSection) {
          window.history.pushState(
            {},
            '',
            `${window.location.pathname}?scroll-to=${contactSection}&previous-tab=${staffApplicationStatusesIndexMapping[oldItem.columnId]}`
          )
        }
      } else {
        window.history.pushState(
          {},
          '',
          `${window.location.pathname}?previous-tab=${staffApplicationStatusesIndexMapping[oldItem.columnId]}`
        )
      }
    }

    // if the columnId is not PAID and the columnId is not the same as the newItem.columnId
    // we are checking if it is different from the newItem.columnId since we updated it the first one
    for (const { id, columnId, order } of updatedData) {
      // locked applications are skipped: reordering the "Entretien terminé" column must not
      // rewrite their status to the column they are only displayed in
      if (columnId !== 'PAID' && id !== newItem.id && !isLockedApplication(Number(id))) {
        await updateStaffApplicationStatus({ id: Number(id), status: columnId, order })
      }
    }

    await refetchApplications()

    return true
  }

  const data = React.useMemo<KanbanProps[]>(
    () =>
      applications?.data
        ?.map((application) => ({
          id: application.id.toString(),
          columnId:
            application.currentScoreCard?.paidToPCR || application.pcrScoreCard?.paidToPCR
              ? 'PAID'
              : pcrKanbanLockedStatuses.has(application.status)
                ? StaffApplicationStatusEnum.INTERVIEW_DONE
                : (application.status as StaffApplicationKanbanStatus),
          data: application
        }))
        .sort((a, b) => a.data.order - b.data.order) || [],
    [applications]
  )

  const columns: KanbanColumnProps[] = React.useMemo(
    () =>
      (
        Object.entries(staffApplicationKanbanStatusMapping) as [
          StaffApplicationKanbanStatus,
          (typeof staffApplicationKanbanStatusMapping)[StaffApplicationKanbanStatus]
        ][]
      ).map(([key, value]) => {
        const shouldDisplayTotal = pcrSalaryTotalStatuses.has(key)

        const salaryTotal = data
          .filter((item) => item.columnId === key)
          .reduce((total, item) => {
            if (key === 'PAID') {
              return (
                total +
                (item.data.currentScoreCard?.amountPaidToPCR ??
                  item.data.pcrScoreCard?.amountPaidToPCR ??
                  0)
              )
            }

            return total + (getPcrApplicationSalary(item.data, payScale) ?? 0)
          }, 0)

        return {
          id: key,
          icon: value.icon,
          title: value.label,
          isDropDisabled: key === 'PAID',
          subtitle:
            key === 'PAID'
              ? `Total payé = ${formatPcrSalary(salaryTotal)}`
              : shouldDisplayTotal
                ? `Total à payer = ${formatPcrSalary(salaryTotal)}`
                : undefined
        }
      }),
    [data, payScale]
  )

  useEffect(() => {
    // Update search state only when debouncedSearch changes
    setSearch(debouncedSearch)
  }, [debouncedSearch])

  return (
    <>
      <div className={'h-full'}>
        <UserNavBar
          title={`${user?.role === RoleEnum.ADMIN && pcr ? `${StringFormatter.capitalize(pcr.firstName)} ${pcr.lastName.toUpperCase()} - Tableau de bord` : 'Tableau de bord'}`}
          icon={HeaderIcon}
        />
        <div className="h-4" />
        <Box className="p-5">
          <div className="flex justify-between items-center py-2 flex-wrap gap-2">
            <Typography variant="sub-heading">Candidats et statuts</Typography>
            <Input
              type="text"
              options={{ clear: true }}
              icon={SearchIcon}
              wrapperClassName="w-64"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </div>
          <div className="h-2" />
          <NoteBox>
            <Typography variant="small-semibold" color={tailwindColors.primary.DEFAULT}>
              Ce tableau de bord vous permet de gérer vos candidatures à travers différentes
              colonnes. Chaque nouvelle candidature apparaît dans la première colonne "Candidature
              reçue".
              <br />
              Vous pouvez facilement glisser-déposer les candidatures d'une colonne à l'autre en
              fonction de leur avancement.
              <br />
              Par exemple, dès que vous avez contacté un candidat pour la première fois, vous
              pouvez déplacer sa candidature de "Candidature reçue" à "Premier contact établi",
              puis continuer ainsi jusqu'à
              <br />
              l'entretien ou jusqu'à classer la candidature en "Sans suite".
            </Typography>
          </NoteBox>
          <div className="h-4" />

          <ScrollableDiv disableScroll={disableScroll}>
            <Kanban<KanbanProps>
              columns={columns}
              data={useMemo(
                () =>
                  data.map((item) => ({
                    ...item,
                    columnId: item.columnId
                  })),
                [data]
              )}
              gap={14}
              card={KanbanTaskCard}
              onDragStart={() => setDisableScroll(true)}
              onDragEnd={() => setDisableScroll(false)}
              onUpdate={handleUpdateChange}
            />
          </ScrollableDiv>
        </Box>
      </div>
      {selectedData && scoreCard && !isFetching && (
        <PcrScoreCardModal
          defaultTab={
            scoreCard.application.status === StaffApplicationStatusEnum.CLASSIFIED
              ? previousTab
              : staffApplicationStatusesIndexMapping[scoreCard.application.status]
          }
          status={scoreCard.paidToPCR ? 'PAID' : scoreCard.application.status}
          isOpen={modalState.scoreCard}
          onCancel={() => closeModel('scoreCard')}
          staffApplication={scoreCard.application}
          defaultValues={mapScoreCardToInput(scoreCard)}
          handleSubmit={(values) => mutate({ id: scoreCard.id, ...values })}
          isLoading={isPending}
          handleReset={() => closeModel('scoreCard')}
        />
      )}
    </>
  )
}

export default PcrKanbanPage
